import React, { useRef, useState } from 'react'

import { MAPS_NO_DATA_LOCATION } from '../constants'
import { MapCoordinates } from '../types'

export interface GooglePlaceItem {
   mainText: string
   secondaryText: string
   placeId: string
   prediction: google.maps.places.PlacePrediction
}

interface UseGooglePlacesSearchProps {
   locationBias?: google.maps.LatLngLiteral
   onLocationSelect: (coordinates: MapCoordinates) => void
   onInputChange: (value: string) => void
}

interface UseGooglePlacesSearchReturn {
   googlePlacesData: GooglePlaceItem[]
   fetchSuggestions: (
      page: number,
      filterText?: string
   ) => Promise<GooglePlaceItem[]>
   onSelectPlace: (key: React.Key) => Promise<void>
}

const useGooglePlacesSearch = (
   props: UseGooglePlacesSearchProps
): UseGooglePlacesSearchReturn => {
   const { locationBias, onLocationSelect, onInputChange } = props

   const [googlePlacesData, setGooglePlacesData] = useState<GooglePlaceItem[]>(
      []
   )

   const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(
      null
   )

   const fetchSuggestions = async (
      _page: number,
      filterText?: string
   ): Promise<GooglePlaceItem[]> => {
      if (!filterText?.length) return []
      try {
         if (!sessionTokenRef.current) {
            sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken()
         }

         const {
            suggestions
         } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            {
               input: filterText,
               sessionToken: sessionTokenRef.current,
               locationBias: new google.maps.LatLngBounds(
                  locationBias ?? MAPS_NO_DATA_LOCATION
               )
            }
         )

         const results: GooglePlaceItem[] = suggestions.flatMap(s => {
            const prediction = s.placePrediction
            if (!prediction) return []
            return [
               {
                  mainText: prediction.mainText?.text ?? prediction.text.text,
                  secondaryText: prediction.secondaryText?.text ?? '',
                  placeId: prediction.placeId,
                  prediction
               }
            ]
         })

         setGooglePlacesData(results)
         return results
      } catch {
         return []
      }
   }

   const onSelectPlace = async (key: React.Key): Promise<void> => {
      const selectedItem = googlePlacesData.find(item => item.placeId === key)
      if (!selectedItem) return

      onInputChange(selectedItem.mainText)
      sessionTokenRef.current = null

      try {
         const place = selectedItem.prediction.toPlace()
         await place.fetchFields({
            fields: ['location', 'displayName', 'formattedAddress']
         })

         if (!place.location) return

         onLocationSelect({
            lat: place.location.lat(),
            lng: place.location.lng()
         })
      } catch {
         // place details unavailable — silently ignore
      }
   }

   return { googlePlacesData, fetchSuggestions, onSelectPlace }
}

export default useGooglePlacesSearch
